import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import Api from "api/Api";
import Toaster from "components/common/Toaster";
import moment from "moment";

const ApiKeyManager = ({ storeId, storeName }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDesc] = useState("");
  const [quota, setQuota] = useState(100);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null); // show once after create
  const [rotated, setRotated] = useState(null); // { oldKey, newKeyValue }
  const [showKeyId, setShowKeyId] = useState(null); // which key's full value is visible
  const toaster = useRef();

  useEffect(() => {
    fetchKeys();
  }, [storeId]);

  const fetchKeys = () => {
    setLoading(true);
    const q = storeId ? `?storesId=${storeId}` : "";
    Api.get(`admin/apiKey/list${q}`)
      .then(r => {
        setKeys(r.data.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const handleCreate = () => {
    if (!name.trim()) {
      toaster.current && toaster.current.error("Name is required");
      return;
    }
    setCreating(true);
    Api.post("admin/apiKey/create", {
      storesId: storeId || null,
      name,
      description,
      quotaPerHour: quota
    })
      .then(r => {
        setNewKey(r.data.keyValue);
        setName("");
        setDesc("");
        setQuota(100);
        toaster.current && toaster.current.success("API key created!");
        fetchKeys();
      })
      .catch(
        e =>
          toaster.current &&
          toaster.current.error(e.response?.data?.message || "Failed to create")
      )
      .finally(() => setCreating(false));
  };

  const handleRevoke = (id, keyName) => {
    if (!window.confirm(`Revoke key "${keyName}"? This cannot be undone.`))
      return;
    Api.delete(`admin/apiKey/revoke?id=${id}`)
      .then(() => {
        toaster.current && toaster.current.success("Key revoked");
        fetchKeys();
      })
      .catch(() => toaster.current && toaster.current.error("Revoke failed"));
  };

  const handleRotate = key => {
    if (
      !window.confirm(
        `Rotate key "${key.name}"?\n\nA new key will be created with the same settings. The old key will remain active until you revoke it.`
      )
    )
      return;
    Api.post("admin/apiKey/create", {
      storesId: key.storesId || null,
      name: key.name + " (Rotated)",
      description: key.description || "Rotated from " + key.name,
      quotaPerHour: key.quotaPerHour || 100
    })
      .then(r => {
        setRotated({
          oldKey: key,
          newKeyValue: r.data.keyValue
        });
        toaster.current &&
          toaster.current.success("Key rotated — new key shown below");
        fetchKeys();
      })
      .catch(
        e =>
          toaster.current &&
          toaster.current.error(e.response?.data?.message || "Rotate failed")
      );
  };

  const handleDelete = (id, name) => {
    if (
      !window.confirm(
        `Delete key "${name}" permanently?\n\nThis action cannot be undone. The key will be permanently removed from the database.`
      )
    )
      return;
    Api.delete(`admin/apiKey/delete?id=${id}`)
      .then(() => {
        toaster.current && toaster.current.success("Key deleted permanently");
        fetchKeys();
      })
      .catch(() => toaster.current && toaster.current.error("Delete failed"));
  };

  return (
    <div className="roe-card-style mtb-15">
      <div className="roe-card-header module-header">
        <div className="flex-1 fs-16 demi-bold-text">
          <span className="hash"># </span>
          API Key Management {storeName ? `— ${storeName}` : ""}
        </div>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={fetchKeys}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Context banner */}
      <div
        style={{
          margin: "15px 20px 0",
          padding: "10px 14px",
          borderRadius: 6,
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: storeId ? "#e0f7fa" : "#f3e5f5",
          border: `1px solid ${storeId ? "#b2ebf2" : "#e1bee7"}`
        }}
      >
        <span
          className={`badge badge-${storeId ? "info" : "primary"}`}
          style={{ fontSize: 11 }}
        >
          {storeId ? "Merchant-Level" : "Platform-Level"}
        </span>
        <span style={{ color: "#444" }}>
          {storeId
            ? "Keys created here are scoped to this store only. No X-Store-Code header required."
            : "Keys created here work across all stores. Partners must include X-Store-Code per request."}
        </span>
      </div>
      <div className="roe-card-body" style={{ padding: 24 }}>
        {/* Create form */}
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 8,
            padding: 20,
            marginBottom: 24
          }}
        >
          <h6 className="demi-bold-text mb-3">Create New API Key</h6>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr 120px",
              gap: 12,
              marginBottom: 12
            }}
          >
            <div>
              <label style={{ fontSize: 12, color: "#555" }}>Name *</label>
              <input
                className="form-control form-control-sm"
                placeholder="e.g. Delivery App"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#555" }}>Description</label>
              <input
                className="form-control form-control-sm"
                placeholder="Optional description"
                value={description}
                onChange={e => setDesc(e.target.value)}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#555" }}>
                Quota / hour
              </label>
              <input
                type="number"
                className="form-control form-control-sm"
                value={quota}
                min={1}
                onChange={e => setQuota(parseInt(e.target.value) || 100)}
              />
            </div>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? "Creating…" : "＋ Create API Key"}
          </button>
        </div>

        {/* One-time key reveal */}
        {newKey && (
          <div
            className="alert alert-success"
            style={{ fontFamily: "monospace", fontSize: 13 }}
          >
            <strong>⚠ Copy this key now — it will not be shown again:</strong>
            <br />
            <span style={{ fontSize: 15, letterSpacing: 1 }}>{newKey}</span>
            <button
              className="btn btn-sm btn-outline-success ml-3"
              onClick={() => {
                navigator.clipboard.writeText(newKey);
                toaster.current && toaster.current.success("Copied!");
              }}
            >
              Copy
            </button>
            <button
              className="btn btn-sm btn-outline-secondary ml-2"
              onClick={() => setNewKey(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* How to use */}
        <div
          style={{
            background: "#e8f0fe",
            borderRadius: 6,
            padding: 14,
            marginBottom: 20,
            fontSize: 12
          }}
        >
          <strong>How to use (POS Receipt API):</strong>
          <br />
          <code>
            curl -X POST https://dev-api.greentill.co/api/v1/pos/receipt \<br />
            &nbsp;&nbsp;-H "X-API-Key: gt_live_xxxx" \<br />
            {storeId
              ? ""
              : '&nbsp;&nbsp;-H "X-Store-Code: GT-77-ABC123" <br />'}
            &nbsp;&nbsp;-F "receipt=@receipt.pdf" \<br />
            &nbsp;&nbsp;-F "externalId=TXN-001" \<br />
            &nbsp;&nbsp;-F "paymentMethod=CARD"
          </code>
          <br />
          <span style={{ fontSize: 11, color: "#555" }}>
            {storeId
              ? "→ Merchant-Level key: no X-Store-Code needed (key is scoped to this store)."
              : "→ Platform-Level key: include X-Store-Code for each merchant transaction."}
          </span>
        </div>

        {/* Rotation result */}
        {rotated && (
          <div
            className="alert alert-warning"
            style={{ fontFamily: "monospace", fontSize: 13 }}
          >
            <strong>⚙ Key Rotated — Action Required:</strong>
            <div style={{ marginTop: 8 }}>
              <strong>Old key:</strong>{" "}
              <code>{rotated.oldKey.keyValue.substring(0, 18) + "…"}</code>{" "}
              <span className="badge badge-secondary">
                {rotated.oldKey.storesId ? "Merchant-Level" : "Platform-Level"}
              </span>
            </div>
            <div style={{ marginTop: 4 }}>
              <strong>New key:</strong>{" "}
              <code style={{ fontSize: 15, letterSpacing: 1 }}>
                {rotated.newKeyValue}
              </code>
              <button
                className="btn btn-sm btn-outline-success ml-2"
                onClick={() => {
                  navigator.clipboard.writeText(rotated.newKeyValue);
                  toaster.current && toaster.current.success("Copied new key!");
                }}
              >
                Copy
              </button>
            </div>
            <div style={{ marginTop: 8, fontSize: 11 }}>
              1. Update the POS partner with the new key. 2. Click{" "}
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() =>
                  handleRevoke(rotated.oldKey.apiKeyId, rotated.oldKey.name)
                }
              >
                Revoke Old Key
              </button>{" "}
              when the new key is confirmed working.
            </div>
            <button
              className="btn btn-sm btn-outline-secondary mt-2"
              onClick={() => setRotated(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Keys table */}
        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : keys.length === 0 ? (
          <div className="alert alert-info" style={{ fontSize: 13 }}>
            No API keys yet. Create one above.
          </div>
        ) : (
          <table
            className="table table-bordered table-sm"
            style={{ fontSize: 13 }}
          >
            <thead style={{ background: "#f1f3f4" }}>
              <tr>
                <th>Name</th>
                <th>Scope</th>
                <th>Key (prefix)</th>
                <th>Quota/hr</th>
                <th>Total Requests</th>
                <th>Last Used</th>
                <th>Created</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.apiKeyId} style={{ opacity: k.isActive ? 1 : 0.5 }}>
                  <td>
                    <strong>{k.name}</strong>
                    {k.description && (
                      <div style={{ fontSize: 11, color: "#888" }}>
                        {k.description}
                      </div>
                    )}
                  </td>
                  <td>
                    <span
                      className={`badge badge-${
                        k.storesId ? "info" : "primary"
                      }`}
                      style={{ fontSize: 10, fontWeight: 500 }}
                    >
                      {k.storesId ? "Merchant-Level" : "Platform-Level"}
                    </span>
                    {k.storesId && k.storeName && (
                      <div
                        style={{ fontSize: 11, color: "#555", marginTop: 3 }}
                      >
                        <i
                          className="fas fa-store"
                          style={{ fontSize: 10, marginRight: 4 }}
                        />
                        {k.storeName}
                        {k.storeCode && (
                          <span style={{ color: "#888", marginLeft: 4 }}>
                            ({k.storeCode})
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <code style={{ fontSize: 11 }}>
                        {showKeyId === k.apiKeyId
                          ? k.keyValue
                          : k.keyValue
                          ? k.keyValue.substring(0, 18) + "…"
                          : "—"}
                      </code>
                      {k.keyValue && (
                        <button
                          className="btn btn-xs btn-outline-secondary"
                          style={{ padding: "2px 6px", fontSize: 10 }}
                          onClick={() =>
                            setShowKeyId(
                              showKeyId === k.apiKeyId ? null : k.apiKeyId
                            )
                          }
                        >
                          <i
                            className={`fas fa-eye${
                              showKeyId === k.apiKeyId ? "-slash" : ""
                            }`}
                          />
                        </button>
                      )}
                      {k.keyValue && (
                        <button
                          className="btn btn-xs btn-outline-success"
                          style={{ padding: "2px 6px", fontSize: 10 }}
                          onClick={() => {
                            navigator.clipboard.writeText(k.keyValue);
                            toaster.current &&
                              toaster.current.success("Key copied!");
                          }}
                        >
                          <i className="fas fa-copy" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td>{k.quotaPerHour}</td>
                  <td>{k.totalRequests}</td>
                  <td>
                    {k.lastUsedAt ? moment(k.lastUsedAt).fromNow() : "Never"}
                  </td>
                  <td>
                    {k.createdAt ? moment(k.createdAt).format("DD/MM/YY") : "—"}
                  </td>
                  <td>
                    {k.isActive ? (
                      <span style={{ color: "#28a745", fontWeight: 600 }}>
                        ✓ Active
                      </span>
                    ) : (
                      <span style={{ color: "#dc3545" }}>✗ Revoked</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      {k.isActive && (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => handleRotate(k)}
                        >
                          Rotate
                        </button>
                      )}
                      {k.isActive && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRevoke(k.apiKeyId, k.name)}
                        >
                          Revoke
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={() => handleDelete(k.apiKeyId, k.name)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Toaster ref={toaster} />
    </div>
  );
};

const mapStateToProps = state => ({ ...state.themeChanger, ...state.storeID });
export default connect(mapStateToProps)(ApiKeyManager);
